/* Native touch scrolling for genuine product media; mouse and keyboard enhancement. */
(() => {
  'use strict';
  const namespace = '__enginoraProductGallery';
  if (window[namespace]) { window[namespace].mount(document); return; }
  const instances = new WeakMap();
  const selector = '.product-media-track';

  function init(track) {
    if (instances.has(track)) return;
    const media = Array.from(track.children).filter((item) => item.classList.contains('product-media'));
    // One actual image stays one image: no duplicate slide, controls or artificial gesture.
    if (media.length < 2) return;
    const gallery = track.closest('.product-gallery');
    const section = track.closest('[data-product-section]');
    const thumbs = gallery ? Array.from(gallery.querySelectorAll('.product-thumbnails a')) : [];
    const title = section?.querySelector('h1')?.textContent.trim();
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const controller = new AbortController();
    const status = document.createElement('p');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    gallery?.appendChild(status);
    track.dataset.gallerySwipe = 'true';
    track.tabIndex = 0;
    track.setAttribute('role', 'group');
    if (title) track.setAttribute('aria-label', title);
    let current = 0;
    let drag = null;
    let frame = null;
    let settleTimer = null;
    let suppressClickUntil = 0;

    function nearestIndex() {
      const trackRect = track.getBoundingClientRect();
      const center = trackRect.left + trackRect.width / 2;
      let chosen = 0;
      let distance = Infinity;
      media.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        const value = Math.abs(rect.left + rect.width / 2 - center);
        if (value < distance) { distance = value; chosen = index; }
      });
      return chosen;
    }

    function update(announce) {
      current = nearestIndex();
      const targetId = media[current].id;
      thumbs.forEach((thumb) => {
        if (thumb.hash === '#' + targetId) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (announce) {
        const thumb = thumbs.find((item) => item.hash === '#' + targetId);
        status.textContent = thumb?.getAttribute('aria-label') || '';
      }
    }

    function go(target) {
      const index = Math.max(0, Math.min(media.length - 1, target));
      const delta = media[index].getBoundingClientRect().left - track.getBoundingClientRect().left;
      track.scrollTo({left:track.scrollLeft + delta, behavior:motion.matches ? 'auto' : 'smooth'});
      current = index;
    }

    function listen(target, type, handler, options = {}) {
      target.addEventListener(type, handler, { ...options, signal:controller.signal });
    }

    function endDrag(snap) {
      if (!drag) return;
      const completed = drag;
      drag = null;
      track.removeAttribute('data-gallery-dragging');
      if (track.hasPointerCapture?.(completed.pointerId)) track.releasePointerCapture(completed.pointerId);
      if (completed.horizontal) {
        suppressClickUntil = Date.now() + 500;
        if (snap) go(nearestIndex());
      }
    }

    // Touch and pen use the browser's scrolling/scroll-snap, preserving vertical scroll and pinch zoom.
    listen(track, 'pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || event.isPrimary === false ||
          !event.target.closest('.product-media img')) return;
      drag = {pointerId:event.pointerId,x:event.clientX,y:event.clientY,start:track.scrollLeft,horizontal:false,vertical:false};
    }, { passive:true });
    listen(window, 'pointermove', (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.horizontal && !drag.vertical) {
        if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx) * 1.15) drag.vertical = true;
        else if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy) * 1.15) {
          drag.horizontal = true;
          track.setPointerCapture?.(event.pointerId);
          track.setAttribute('data-gallery-dragging','true');
        }
      }
      if (!drag.horizontal) return;
      if (event.cancelable) event.preventDefault();
      track.scrollLeft = drag.start - dx;
    }, { passive:false });
    listen(window,'pointerup',(event) => {if (drag && event.pointerId === drag.pointerId) endDrag(true);});
    listen(window,'pointercancel',(event) => {if (drag && event.pointerId === drag.pointerId) endDrag(true);});
    listen(track,'dragstart',(event) => {if (event.target.closest('.product-media img')) event.preventDefault();});
    listen(track,'click',(event) => {
      if (Date.now() < suppressClickUntil && event.target.closest('.product-media img')) {
        suppressClickUntil=0;
        event.preventDefault();
        event.stopPropagation();
      }
    }, {capture:true});
    listen(track,'keydown',(event) => {
      if (event.altKey || event.ctrlKey || event.metaKey ||
          event.target.closest('a,button,input,select,textarea,video,iframe,model-viewer,[contenteditable="true"]')) return;
      const rtl = window.getComputedStyle(track).direction === 'rtl';
      let target = null;
      if (event.key === 'ArrowRight') target = nearestIndex() + (rtl ? -1 : 1);
      if (event.key === 'ArrowLeft') target = nearestIndex() + (rtl ? 1 : -1);
      if (event.key === 'Home') target = 0;
      if (event.key === 'End') target = media.length - 1;
      if (target === null) return;
      event.preventDefault();
      go(target);
    });
    listen(track,'scroll',() => {
      if (frame === null) frame = window.requestAnimationFrame(() => {frame=null;update(false);});
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => update(true),160);
    }, {passive:true});
    instances.set(track, {destroy() {
      endDrag(false);
      controller.abort();
      window.clearTimeout(settleTimer);
      if (frame !== null) window.cancelAnimationFrame(frame);
      status.remove();
      instances.delete(track);
    }});
    update(false);
  }

  function rootsWithin(scope) {
    const roots = Array.from(scope.querySelectorAll(selector));
    if (scope.matches?.(selector)) roots.unshift(scope);
    return roots;
  }
  function mount(scope) {rootsWithin(scope).forEach(init);}
  window[namespace] = {mount};
  mount(document);
  document.addEventListener('shopify:section:load',(event) => mount(event.target));
  document.addEventListener('shopify:section:unload',(event) => rootsWithin(event.target).forEach((track) => instances.get(track)?.destroy()));
})();
